/**
 * Hijri Calendar Page
 * Displays the full month calendar and allows selecting calculation preference.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getHijriCalendar, 
  getHijriPreference, 
  saveHijriPreference, 
  type HijriCalendarResponse 
} from '@/services/hijri.service';
import { ArrowLeft, ChevronLeft, ChevronRight, Settings2, Check, ChevronsUpDown } from 'lucide-react';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { toast } from 'react-hot-toast';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import provincesData from '@/api/thailand-provinces-complete.json';

const toArabicNumerals = (num: number) => {
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return num.toString().replace(/[0-9]/g, (w) => arabicDigits[+w]);
};

export function HijriCalendarPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [calendar, setCalendar] = useState<HijriCalendarResponse | null>(null);
  const [preference, setPreference] = useState<-1 | 0>(-1);
  const [city, setCity] = useState('Pattani');
  const [isPrefLoading, setIsPrefLoading] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);

  // Load preferences and current month calendar
  useEffect(() => {
    async function loadInitial() {
      try {
        const [prefData, calData] = await Promise.all([
          getHijriPreference(),
          getHijriCalendar()
        ]);
        const HAS_SET_PREFERENCE_KEY = 'quran_assistant_hijri_pref_set';
        const hasSetPreference = localStorage.getItem(HAS_SET_PREFERENCE_KEY) === 'true';

        if (prefData) {
          setPreference(prefData.adjustment as -1 | 0);
          if (prefData.city) setCity(prefData.city);
          if (!hasSetPreference) {
            setIsSettingsOpen(true);
          }
        } else {
          if (!hasSetPreference) {
            setIsSettingsOpen(true);
          }
        }
        if (calData) setCalendar(calData);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    loadInitial();
  }, []);

  // Change month (1-12). If wrapping around, year changes too.
  const handleMonthChange = async (offset: number) => {
    if (!calendar) return;
    setLoading(true);
    let newMonth = calendar.month + offset;
    let newYear = calendar.year;

    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }

    const data = await getHijriCalendar(newMonth, newYear);
    if (data) setCalendar(data);
    setLoading(false);
  };

  // Save preference button handler
  const handleSaveBtnClick = async () => {
    setIsPrefLoading(true);
    const result = await saveHijriPreference(preference, city, 'Thailand');
    setIsPrefLoading(false);
    
    if (result) {
      localStorage.setItem('quran_assistant_hijri_pref_set', 'true');
      setPreference(result.adjustment as -1 | 0);
      setCity(result.city || 'Pattani');
      toast.success(`บันทึกการตั้งค่าสำเร็จ`, { position: 'top-center' });
      setIsSettingsOpen(false);
      
      // Reload calendar to reflect new preference
      setLoading(true);
      const data = await getHijriCalendar(calendar?.month, calendar?.year);
      if (data) setCalendar(data);
      setLoading(false);
    } else {
      toast.error('ไม่สามารถบันทึกการตั้งค่าได้', { position: 'top-center' });
    }
  };

  if (!calendar && loading) {
    return <LoadingScreen />;
  }

  // Days mapping
  const daysOfWeekEn = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const daysOfWeekTh = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา']; // map from English index
  const thaiMonthsShort = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  
  // Create padding for first day of the week
  const firstDay = calendar?.days[0];
  const firstDayIndex = daysOfWeekEn.indexOf(firstDay?.weekday || 'Monday');
  const paddingDays = Array.from({ length: firstDayIndex }).map((_, i) => <div key={`pad-start-${i}`} className="p-2" />);
  
  // Current Thai Gregorian Date for highlighting "today" rough check
  const today = new Date();
  const currentGregDate = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-accent focus:outline-none"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <h1 className="text-lg font-semibold text-foreground">ปฏิทินฮิจเราะห์</h1>
        </div>

        <Sheet open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
          <SheetTrigger asChild>
            <button className="p-2 -mr-2 rounded-full hover:bg-accent text-muted-foreground transition">
              <Settings2 className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl px-6 pt-6 pb-12 max-h-[90vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="text-xl">ตั้งค่าปฏิทินฮิจเราะห์</SheetTitle>
              <SheetDescription className="pt-2 text-base">
                เลือกระบบการนับวันตามที่คุณยึดถือ
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 flex flex-col gap-6">
              <RadioGroup 
                value={String(preference)} 
                onValueChange={(val: string) => setPreference(parseInt(val) as -1 | 0)}
              >
                <div className="flex items-center justify-between p-4 rounded-xl border border-border/50 bg-card hover:bg-accent/50 cursor-pointer transition" onClick={() => setPreference(-1)}>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="r-th" className="text-base font-semibold cursor-pointer pointer-events-none">เดือนใน (ตามภูมิภาคไทย)</Label>
                    <span className="text-sm text-muted-foreground">-1 วันจากการนับเดือนนอก</span>
                  </div>
                  <RadioGroupItem value="-1" id="r-th" disabled={isPrefLoading} />
                </div>
                
                <div className="flex items-center justify-between p-4 rounded-xl border border-border/50 bg-card hover:bg-accent/50 cursor-pointer transition mt-2" onClick={() => setPreference(0)}>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="r-sa" className="text-base font-semibold cursor-pointer pointer-events-none">เดือนนอก (สากล/ซาอุดีอาระเบีย)</Label>
                    <span className="text-sm text-muted-foreground">ตรงตามการนับเดือนนอก</span>
                  </div>
                  <RadioGroupItem value="0" id="r-sa" disabled={isPrefLoading} />
                </div>
              </RadioGroup>

              <div className="space-y-3">
                <Label className="text-base font-semibold">สถานที่สำหรับคำนวณเวลา</Label>
                <div className="flex flex-col gap-4">
                  <div className="space-y-2 flex flex-col">
                    <Label className="text-sm text-muted-foreground">จังหวัด</Label>
                    <Popover open={isComboboxOpen} onOpenChange={setIsComboboxOpen} modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={isComboboxOpen}
                          className="justify-between w-full font-normal"
                        >
                          {city
                            ? provincesData.provinces.find((p) => p.nameEn.toLowerCase() === city.toLowerCase() || p.nameTh === city)?.nameTh ?? city
                            : 'เลือกจังหวัด...'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="ค้นหาจังหวัด..." />
                          <CommandList>
                            <CommandEmpty>ไม่พบจังหวัด</CommandEmpty>
                            <CommandGroup>
                              {provincesData.provinces.map((province) => (
                                <CommandItem
                                  key={province.id}
                                  value={province.nameTh}
                                  onSelect={(currentValue) => {
                                      // CommandItem value is always lowercase in Shadcn by default based on search, 
                                      // so we use the exact object ref to get exact spelling
                                      const selectedProv = provincesData.provinces.find(p => p.nameTh.toLowerCase() === currentValue.toLowerCase());
                                      setCity(selectedProv?.nameEn || province.nameEn);
                                      setIsComboboxOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      (city.toLowerCase() === province.nameEn.toLowerCase() || city === province.nameTh) ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {province.nameTh} ({province.nameEn})
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button 
                  onClick={handleSaveBtnClick} 
                  disabled={isPrefLoading}
                  className="w-full text-base font-medium h-12 rounded-xl"
                >
                  {isPrefLoading ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      {calendar && (
        <main className="flex-1 p-4 pb-24">
          {/* Month Navigation */}
          <div className="relative overflow-hidden flex items-center justify-between mb-8 rounded-[2.5rem] px-4 py-5 shadow-sm border border-primary/20 bg-linear-to-br from-primary/10 via-primary/5 to-transparent">
            {/* Decorative elements */}
            <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl z-0" />
            <div className="absolute -left-10 -top-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl z-0" />
            
            <button 
              onClick={() => handleMonthChange(-1)}
              className="relative z-10 p-3 rounded-full bg-background hover:bg-accent text-foreground transition shadow-sm"
              disabled={loading}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="relative z-10 text-center flex flex-col items-center justify-center">
              <div className="flex items-center gap-2 text-2xl font-bold text-foreground mb-1">
                <span className="font-sans font-bold">{calendar.year}</span>
                <span className="font-arabic" dir="rtl">{calendar.monthNameArabic}</span>
              </div>
              <div className="text-[15px] font-medium text-foreground/80">
                {calendar.monthName} (AH)
              </div>
            </div>
            <button 
              onClick={() => handleMonthChange(1)}
              className="relative z-10 p-3 rounded-full bg-background hover:bg-accent text-foreground transition shadow-sm"
              disabled={loading}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className={`transition-opacity duration-300 ${loading ? 'opacity-50' : 'opacity-100'}`}>
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-3">
              {daysOfWeekTh.map((day) => (
                <div key={day} className="text-center text-sm font-bold text-foreground py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {paddingDays}
              {calendar.days.map((day, idx) => {
                const isTodayStr = day.gregorianDate === currentGregDate;
                
                return (
                  <div 
                    key={idx}
                    className={`aspect-square flex flex-col justify-center items-center rounded-[1.25rem] border relative transition-colors ${
                      isTodayStr 
                        ? 'bg-primary text-primary-foreground border-primary shadow-md' 
                        : 'bg-card border-border/50 hover:bg-accent/50 text-foreground'
                    }`}
                  >
                    <span className={`text-xl sm:text-2xl font-extrabold leading-none mb-1 font-arabic ${isTodayStr ? 'text-primary-foreground' : 'text-foreground'}`}>
                      {toArabicNumerals(day.hijriDay)}
                    </span>
                    <span className={`text-[10px] sm:text-[11px] font-medium leading-none ${isTodayStr ? 'text-primary-foreground/90' : 'text-muted-foreground'}`}>
                      {day.gregorianDay} {thaiMonthsShort[day.gregorianMonth - 1]}
                    </span>
                    
                    {/* Add small Hijri month indicator on the 1st of the month */}
                    {day.hijriDay === 1 && (
                      <span className={`absolute top-1.5 right-1.5 text-[7px] sm:text-[8px] font-arabic opacity-80 ${isTodayStr ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                        {day.hijriMonthNameArabic.split(' ')[0]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

export default HijriCalendarPage;
